import React, { useState, useEffect } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  AreaChart, Area, ComposedChart, Scatter
} from 'recharts';
import './inventory-comparison.css';

const StockDashboard = () => {
  // État pour la période sélectionnée
  const [dateRange, setDateRange] = useState({
    start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });

  // États pour les données
  const [comparisonData, setComparisonData] = useState(null);
  const [lossesData, setLossesData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeView, setActiveView] = useState('overview'); // 'overview', 'losses', 'trends', 'details', 'ingredients', 'locations'
  const [selectedMetric, setSelectedMetric] = useState('difference');
  const [expandedIngredient, setExpandedIngredient] = useState(null);
  const [ingredientDetails, setIngredientDetails] = useState({});

  // États pour les localisations
  const [locationsData, setLocationsData] = useState({});
  const [selectedLocation, setSelectedLocation] = useState('all');
  const [locations, setLocations] = useState([]);
  const [locationsList, setLocationsList] = useState([]);
  const [fetchError, setFetchError] = useState(null);

  // Couleurs pour les graphiques
  const CHART_COLORS = {
    primary: '#3498db',
    secondary: '#2ecc71',
    danger: '#e74c3c',
    warning: '#f39c12',
    info: '#9b59b6',
    success: '#27ae60',
    dark: '#2c3e50',
    light: '#ecf0f1'
  };

  const PIE_COLORS = [CHART_COLORS.danger, CHART_COLORS.success, CHART_COLORS.warning, CHART_COLORS.info, CHART_COLORS.primary];

  const safeSort = (array, compareFn) => {
    if (!array || !Array.isArray(array)) return [];
    return [...array].sort(compareFn);
  };

  const token = () => localStorage.getItem("token") || sessionStorage.getItem("token");

  // Charger les localisations depuis l'API (source de vérité pour le filtre)
  useEffect(() => {
    const loadLocations = async () => {
      if (!token()) return;
      try {
        const res = await axios.get('/api/Locationspin/getAllLocations', {
          headers: { 'Authorization': `Bearer ${token()}` }
        });
        const list = Array.isArray(res.data) ? res.data : (res.data?.locations || []);
        setLocationsList(list.map((l) => ({ id: l._id, name: l.name || `Location ${(l._id || '').slice(-6)}` })));
      } catch (e) {
        console.warn("Erreur chargement localisations:", e?.response?.data || e.message);
        setLocationsList([]);
      }
    };
    loadLocations();
  }, []);

  // Charger les données quand la période ou la localisation change
  useEffect(() => {
    fetchDashboardData();
  }, [dateRange.start, dateRange.end, selectedLocation]);

  const fetchDashboardData = async () => {
    setLoading(true);
    setFetchError(null);
    try {
      if (!token()) {
        setFetchError("Authentification requise");
        setComparisonData(null);
        setLossesData(null);
        setIngredientDetails({});
        setLocationsData({});
        setLocations([]);
        setLoading(false);
        return;
      }

      const params = {
        startDate: dateRange.start,
        endDate: dateRange.end
      };
      if (selectedLocation && selectedLocation !== 'all') {
        params.locationId = selectedLocation;
      }

      const comparisonRes = await axios.get('/api/daily-inventory/report/comparison', {
        params,
        headers: { 'Authorization': `Bearer ${token()}` }
      });

      const lossesRes = await axios.get('/api/daily-inventory/report/stock-losses', {
        params,
        headers: { 'Authorization': `Bearer ${token()}` }
      });

      if (comparisonRes.data.success) {
        setComparisonData(comparisonRes.data);
        if (comparisonRes.data.dailyAnalysis && comparisonRes.data.dailyAnalysis.length > 0) {
          prepareIngredientDetails(comparisonRes.data);
        } else {
          setIngredientDetails({});
          setLocationsData({});
          setLocations([]);
        }
      } else {
        setComparisonData(null);
        setIngredientDetails({});
        setLocationsData({});
        setLocations([]);
      }

      if (lossesRes.data.success) {
        setLossesData(lossesRes.data);
      } else {
        setLossesData(null);
      }
    } catch (error) {
      console.error("Erreur chargement dashboard:", error?.response?.data || error?.message);
      setFetchError(error?.response?.data?.message || error?.message || "Erreur lors du chargement");
      setComparisonData(null);
      setLossesData(null);
      setIngredientDetails({});
      setLocationsData({});
      setLocations([]);
    } finally {
      setLoading(false);
    }
  };

  // Modifier la fonction prepareIngredientDetails
  const prepareIngredientDetails = (data) => {
    if (!data || !data.dailyAnalysis || !Array.isArray(data.dailyAnalysis)) {
      console.log("❌ Aucune donnée d'analyse quotidienne disponible");
      return;
    }

    console.log("📊 Préparation des détails d'ingrédients avec localisation");

    const details = {};
    const locations = {};

    data.dailyAnalysis.forEach(day => {
      console.log(`\nJour: ${day.date}`);

      // 1. Traiter les ingrédients
      const dayIngredients = day.detailsByIngredient || {};

      Object.keys(dayIngredients).forEach(ingId => {
        const ingredient = dayIngredients[ingId];

        if (!ingredient) return;

        const actualId = ingredient.ingredientId || ingId;
        const ingredientName = ingredient.ingredientName || `Ingrédient ${actualId.slice(-6)}`;

        if (!details[actualId]) {
          details[actualId] = {
            id: actualId,
            name: ingredientName,
            unit: ingredient.unit || 'unité',
            category: ingredient.category || 'Non catégorisé',
            days: [],
            locations: {},
            totalCurrent: 0,
            totalRecorded: 0,
            totalDifference: 0,
            totalLossValue: 0,
            averagePrice: 0,
            averageDifference: 0,
            consistency: 0
          };
        }

        const currentStock = ingredient.totalCurrent || 0;
        const recordedQuantity = ingredient.totalRecorded || 0;
        const difference = ingredient.totalDifference || 0;
        const lossValue = parseFloat(ingredient.totalLossValue) || 0;
        const avgPrice = parseFloat(ingredient.averagePrice) || 0;

        details[actualId].days.push({
          date: day.date,
          currentStock,
          recordedQuantity,
          difference,
          percentage: currentStock > 0
            ? ((difference / currentStock) * 100).toFixed(2)
            : '0.00'
        });

        details[actualId].totalCurrent += currentStock;
        details[actualId].totalRecorded += recordedQuantity;
        details[actualId].totalDifference += difference;
        details[actualId].totalLossValue += lossValue;
        if (avgPrice > 0) details[actualId].averagePrice = avgPrice;
      });

      // 2. Traiter les localisations
      const dayLocations = day.detailsByLocation || {};

      Object.keys(dayLocations).forEach(locId => {
        const location = dayLocations[locId];

        if (!location) return;

        const locationId = location.locationId || locId;
        const locationName = location.locationName || `Localisation ${locationId.slice(-6)}`;

        if (!locations[locationId]) {
          locations[locationId] = {
            id: locationId,
            name: locationName,
            days: [],
            totalItems: 0,
            totalDifference: 0,
            totalValueDifference: 0
          };
        }

        // Créer un nouvel objet pour chaque jour (éviter la mutation)
        locations[locationId].days.push({
          date: day.date,
          totalItems: location.totalItems || 0,
          totalDifference: location.totalDifference || 0,
          totalValueDifference: location.totalValueDifference || 0
        });

        locations[locationId].totalItems += location.totalItems || 0;
        locations[locationId].totalDifference += location.totalDifference || 0;
        locations[locationId].totalValueDifference += location.totalValueDifference || 0;
      });
    });

    // Calculer les moyennes et la consistance pour les ingrédients
    Object.keys(details).forEach(ingId => {
      const ing = details[ingId];

      if (ing.days.length > 0) {
        ing.averageDifference = ing.totalDifference / ing.days.length;
        ing.consistency = calculateConsistency(ing.days.map(d => d.difference));
      }
    });

    // Calculer les moyennes pour les localisations
    Object.keys(locations).forEach(locId => {
      const loc = locations[locId];

      if (loc.days.length > 0) {
        loc.averageDifference = loc.totalDifference / loc.days.length;
        loc.averageValueDifference = loc.totalValueDifference / loc.days.length;
      }
    });

    console.log(`✅ Total ingrédients préparés: ${Object.keys(details).length}`);
    console.log(`✅ Total localisations préparées: ${Object.keys(locations).length}`);

    setIngredientDetails(details);
    setLocationsData(locations);
    setLocations(Object.values(locations));
  };

  // Fonction de calcul de consistance simplifiée - CORRIGÉ
  const calculateConsistency = (differences) => {
    if (!differences || differences.length === 0) return 0;

    // Créer une copie du tableau
    const diffs = [...differences];
    const avg = diffs.reduce((a, b) => a + b, 0) / diffs.length;

    // Si la moyenne est 0, retourner 0
    if (avg === 0) return 0;

    // Calculer l'écart-type
    const squareDiffs = diffs.map(value => Math.pow(value - avg, 2));
    const avgSquareDiff = squareDiffs.reduce((a, b) => a + b, 0) / squareDiffs.length;
    const stdDev = Math.sqrt(avgSquareDiff);

    // Calculer le coefficient de variation (plus petit = plus consistant)
    const coefficientOfVariation = stdDev / Math.abs(avg);

    // Convertir en score de 0-100 (100 = parfaitement consistant)
    const consistencyScore = Math.max(0, 100 - (coefficientOfVariation * 100));

    return parseFloat(consistencyScore.toFixed(1));
  };

  // Gestion du changement de date
  const handleDateChange = (field, value) => {
    setDateRange(prev => ({ ...prev, [field]: value }));
  };

  // Formatage des nombres
  const formatNumber = (num) => {
    if (num === undefined || num === null) return '0';
    return new Intl.NumberFormat('fr-FR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(num);
  };

  // Formatage des valeurs monétaires en DT
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'TND',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const generateReport = () => {
    if (!comparisonData && !lossesData) return;

    const wb = XLSX.utils.book_new();
    const locLabel = selectedLocation !== 'all' && locationsData[selectedLocation]
      ? locationsData[selectedLocation].name
      : 'Toutes';

    // Feuille 1 : Synthèse
    const synthRows = [
      ['RAPPORT DÉTAILLÉ D\'INVENTAIRE'],
      [],
      ['Période', `${dateRange.start} au ${dateRange.end}`],
      ['Date de génération', new Date().toLocaleString('fr-FR')],
      ['Localisation', locLabel],
      [],
      ['SYNTHÈSE GÉNÉRALE'],
    ];
    if (comparisonData?.summary) {
      const s = comparisonData.summary;
      synthRows.push(['Jours analysés', s.totalDays ?? 0]);
      synthRows.push(['Inventaires effectués', s.totalInventories ?? 0]);
      synthRows.push(['Articles inventoriés', s.totalItemsInventoried ?? 0]);
      synthRows.push(['Taux de conformité moyen %', s.averageComplianceRate ?? '0.00']);
      synthRows.push(['Écarts totaux (quantité)', s.totalDiscrepancies ?? 0]);
    }
    synthRows.push([]);
    synthRows.push(['ANALYSE FINANCIÈRE (qté × prix moyen d\'achat)']);
    if (lossesData?.summary) {
      const ls = lossesData.summary.totalLosses || {};
      const lsu = lossesData.summary.totalSurpluses || {};
      const lossVal = Math.abs(ls.value || 0);
      const surVal = lsu.value || 0;
      synthRows.push(['Somme globale des pertes (DT)', lossVal]);
      synthRows.push(['Valeur des surplus (DT)', surVal]);
      synthRows.push(['Impact financier net (DT)', surVal - lossVal]);
      synthRows.push(['Nombre total d\'écarts', lossesData.statistics?.totalDiscrepancies ?? 0]);
    }
    const wsSynth = XLSX.utils.aoa_to_sheet(synthRows);
    XLSX.utils.book_append_sheet(wb, wsSynth, 'Synthèse');

    // Feuille 2 : Pertes par ingrédient
    const byIng = (lossesData?.summary?.byIngredient || []).filter((i) => (i.valueLoss || 0) > 0);
    const pertesRows = [
      ['Ingrédient', 'Quantité perdue', 'Unité', 'Prix moyen (DT)', 'Valeur perte (DT)'],
      ...byIng.map((ing) => [
        ing.ingredientName,
        ing.totalLoss ?? 0,
        ing.unit || '',
        ing.totalLoss ? (ing.valueLoss / ing.totalLoss) : 0,
        ing.valueLoss ?? 0,
      ]),
    ];
    if (byIng.length > 0) {
      pertesRows.push([]);
      pertesRows.push(['Total pertes (DT)', '', '', '', Math.abs(lossesData?.summary?.totalLosses?.value || 0)]);
    }
    const wsPertes = XLSX.utils.aoa_to_sheet(pertesRows.length > 1 ? pertesRows : [['Aucune perte avec valeur sur la période']]);
    XLSX.utils.book_append_sheet(wb, wsPertes, 'Pertes par ingrédient');

    // Feuille 3 : Par localisation
    const sortedLocations = [...(locations || [])].sort((a, b) => b.totalItems - a.totalItems);
    const locRows = [
      ['Localisation', 'Articles total', 'Jours analysés', 'Différence totale', 'Écart moyen/jour', 'Impact valeur (DT)'],
      ...sortedLocations.map((loc) => [
        loc.name,
        loc.totalItems ?? 0,
        loc.days?.length ?? 0,
        loc.totalDifference ?? 0,
        loc.averageDifference ?? 0,
        loc.totalValueDifference ?? 0,
      ]),
    ];
    const wsLoc = XLSX.utils.aoa_to_sheet(locRows.length > 1 ? locRows : [['Aucune donnée de localisation']]);
    XLSX.utils.book_append_sheet(wb, wsLoc, 'Par localisation');

    // Feuille 4 : Détails par ingrédient
    const sortedIngredients = Object.values(ingredientDetails || {})
      .map((ing) => ({ ...ing }))
      .sort((a, b) => Math.abs(b.totalDifference || 0) - Math.abs(a.totalDifference || 0));
    const ingRows = [
      ['Ingrédient', 'Unité', 'Différence totale', 'Moyenne/jour', 'Jours', 'Valeur perte (DT)'],
      ...sortedIngredients.map((ing) => [
        ing.name,
        ing.unit || '',
        ing.totalDifference ?? 0,
        ing.averageDifference ?? 0,
        ing.days?.length ?? 0,
        ing.totalLossValue ?? 0,
      ]),
    ];
    const wsIng = XLSX.utils.aoa_to_sheet(ingRows.length > 1 ? ingRows : [['Aucun détail ingrédient']]);
    XLSX.utils.book_append_sheet(wb, wsIng, 'Par ingrédient');

    // Feuille 5 : Tendances (évolution journalière)
    const daily = comparisonData?.dailyAnalysis || [];
    const trendRows = [
      ['Date', 'Inventaires', 'Articles', 'Écart total', 'Nb. pertes', 'Nb. surplus', 'Conformité %', 'Impact valeur (DT)'],
      ...daily.map((d) => {
        const compliance = d.totalItems > 0 ? ((d.zeroDifferences || 0) / d.totalItems * 100).toFixed(1) : '0.0';
        return [
          d.date,
          Array.isArray(d.inventories) ? d.inventories.length : 0,
          d.totalItems ?? 0,
          d.totalDifference ?? 0,
          d.negativeDifferences ?? 0,
          d.positiveDifferences ?? 0,
          compliance,
          d.totalValueDifference ?? 0,
        ];
      }),
    ];
    const wsTrend = XLSX.utils.aoa_to_sheet(trendRows.length > 1 ? trendRows : [['Aucune donnée de tendances']]);
    XLSX.utils.book_append_sheet(wb, wsTrend, 'Tendances');

    // Feuille 6 : Détails écarts (pertes + surplus)
    const losses = lossesData?.topLosses || [];
    const surpluses = lossesData?.topSurpluses || [];
    const allEcarts = [
      ...losses.map((r) => ({ ...r, type: 'Perte' })),
      ...surpluses.map((r) => ({ ...r, type: 'Surplus' })),
    ].sort((a, b) => (a.date || '').localeCompare(b.date || '') || (b.valueImpact || 0) - (a.valueImpact || 0));
    const ecartRows = [
      ['Date', 'Ingrédient', 'Type', 'Quantité', 'Valeur (DT)', 'Unité', 'Localisation', 'Stock physique', 'Stock enregistré'],
      ...allEcarts.map((r) => [
        r.date,
        r.ingredientName,
        r.type,
        r.difference ?? 0,
        r.valueImpact ?? 0,
        r.unit || '',
        r.location || '',
        r.currentStock ?? 0,
        r.recordedQuantity ?? 0,
      ]),
    ];
    const wsEcart = XLSX.utils.aoa_to_sheet(ecartRows.length > 1 ? ecartRows : [['Aucun écart détaillé']]);
    XLSX.utils.book_append_sheet(wb, wsEcart, 'Détails écarts');

    XLSX.writeFile(wb, `rapport-inventaire-${dateRange.start}-${dateRange.end}.xlsx`);
  };
  // Toggle l'expansion d'un ingrédient
  const toggleIngredientExpansion = (ingredientId) => {
    setExpandedIngredient(expandedIngredient === ingredientId ? null : ingredientId);
  };


  const getTopIngredients = () => {
    if (!ingredientDetails || Object.keys(ingredientDetails).length === 0) return [];

    return Object.values(ingredientDetails)
      .map(ing => ({ ...ing })) // Créer une copie des objets
      .sort((a, b) => Math.abs(b.totalDifference) - Math.abs(a.totalDifference))
      .slice(0, 20);
  };

  const hasData = comparisonData?.dailyAnalysis?.length > 0;
  const isEmpty = !loading && !fetchError && !hasData;

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Chargement des données d'inventaire...</p>
        <small>Cette opération peut prendre quelques secondes</small>
      </div>
    );
  }

  return (
    <div className="stock-dashboard">
      {/* Header principal */}
      <header className="dashboard-header">
        <div className="header-left">
          <h1>
            <span className="icon">📊</span>
            Dashboard d'Inventaire & Stocks
          </h1>
          <p className="subtitle">
            Analyse complète des écarts, pertes et surplus - Montants en DT
          </p>
        </div>

        <div className="header-actions">
          <button className="btn-export" onClick={generateReport}>
            <span className="icon">📊</span>
            Exporter Excel
          </button>
        </div>
      </header>

      {/* Contrôles de période */}
      <section className="period-controls">
        <div className="period-selector">
          <div className="period-input-group">
            <label htmlFor="start-date">
              <span className="icon">📅</span>
              Date de début
            </label>
            <input
              id="start-date"
              type="date"
              value={dateRange.start}
              onChange={(e) => handleDateChange('start', e.target.value)}
              className="date-input"
            />
          </div>

          <div className="period-separator">→</div>

          <div className="period-input-group">
            <label htmlFor="end-date">
              <span className="icon">📅</span>
              Date de fin
            </label>
            <input
              id="end-date"
              type="date"
              value={dateRange.end}
              onChange={(e) => handleDateChange('end', e.target.value)}
              max={new Date().toISOString().split('T')[0]}
              className="date-input"
            />
          </div>

          <button className="btn-primary" onClick={fetchDashboardData}>
            <span className="icon">🔄</span>
            Actualiser
          </button>
        </div>

        {/* AJOUTER CE FILTRE DE LOCALISATION */}
        <div className="location-filter">
          <div className="filter-group">
            <label htmlFor="location-select">
              <span className="icon">📍</span>
              Filtrer par localisation
            </label>
            <select
              id="location-select"
              value={selectedLocation}
              onChange={(e) => setSelectedLocation(e.target.value)}
              className="filter-select"
            >
              <option value="all">Toutes les localisations</option>
              {(locationsList.length ? locationsList : locations).map(location => (
                <option key={location.id} value={location.id}>
                  {location.name}
                </option>
              ))}
            </select>
          </div>

          {/* Afficher les statistiques de localisation sélectionnée */}
          {selectedLocation !== 'all' && locationsData[selectedLocation] && (
            <div className="location-stats">
              <div className="location-stat">
                <span className="stat-label">Localisation:</span>
                <span className="stat-value">{locationsData[selectedLocation].name}</span>
              </div>
              <div className="location-stat">
                <span className="stat-label">Articles total:</span>
                <span className="stat-value">{locationsData[selectedLocation].totalItems}</span>
              </div>
              <div className="location-stat">
                <span className="stat-label">Différence totale:</span>
                <span className={`stat-value ${locationsData[selectedLocation].totalDifference < 0 ? 'negative' :
                    locationsData[selectedLocation].totalDifference > 0 ? 'positive' : 'neutral'
                  }`}>
                  {locationsData[selectedLocation].totalDifference > 0 ? '+' : ''}
                  {formatNumber(locationsData[selectedLocation].totalDifference)}
                </span>
              </div>
            </div>
          )}
        </div>

        <div className="quick-periods">
          <button className="btn-period" onClick={() => {
            const end = new Date();
            const start = new Date();
            start.setDate(start.getDate() - 7);
            setDateRange({
              start: start.toISOString().split('T')[0],
              end: end.toISOString().split('T')[0]
            });
          }}>
            7 derniers jours
          </button>
          <button className="btn-period" onClick={() => {
            const end = new Date();
            const start = new Date();
            start.setDate(start.getDate() - 30);
            setDateRange({
              start: start.toISOString().split('T')[0],
              end: end.toISOString().split('T')[0]
            });
          }}>
            30 derniers jours
          </button>
          <button className="btn-period" onClick={() => {
            const end = new Date();
            const start = new Date();
            start.setMonth(start.getMonth() - 3);
            setDateRange({
              start: start.toISOString().split('T')[0],
              end: end.toISOString().split('T')[0]
            });
          }}>
            3 derniers mois
          </button>
        </div>
      </section>

      {/* Erreur ou état vide */}
      {fetchError && (
        <div className="dashboard-message error">
          <div className="message-icon">⚠️</div>
          <h3>Erreur de chargement</h3>
          <p>{fetchError}</p>
          <button className="btn-primary" onClick={fetchDashboardData}>
            <span className="icon">🔄</span> Réessayer
          </button>
        </div>
      )}

      {!fetchError && isEmpty && (
        <div className="dashboard-message empty">
          <div className="message-icon">📊</div>
          <h3>Aucune donnée d'inventaire pour cette période</h3>
          <p>
            Aucun inventaire journalier trouvé entre <strong>{dateRange.start}</strong> et <strong>{dateRange.end}</strong>
            {selectedLocation !== 'all' ? ` pour la localisation sélectionnée.` : '.'}
          </p>
          <ul>
            <li>Vérifiez la plage de dates (ex. 7 ou 30 derniers jours).</li>
            <li>Choisissez « Toutes les localisations » si vous filtrez par lieu.</li>
            <li>Créez des inventaires depuis <strong>Inventaire journalier</strong> ou <strong>Gestion des inventaires</strong>.</li>
          </ul>
          <button className="btn-primary" onClick={fetchDashboardData}>
            <span className="icon">🔄</span> Actualiser
          </button>
        </div>
      )}

      {/* Navigation, vues et footer (masqués si vide) */}
      {!fetchError && !isEmpty && (
      <>
      <nav className="view-navigation">
        <button
          className={`nav-btn ${activeView === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveView('overview')}
        >
          <span className="icon">🏠</span>
          Vue d'ensemble
        </button>
        <button
          className={`nav-btn ${activeView === 'locations' ? 'active' : ''}`}
          onClick={() => setActiveView('locations')}
        >
          <span className="icon">📍</span>
          Par Localisation
        </button>
        <button
          className={`nav-btn ${activeView === 'losses' ? 'active' : ''}`}
          onClick={() => setActiveView('losses')}
        >
          <span className="icon">📉</span>
          Analyse des Pertes
        </button>
        <button
          className={`nav-btn ${activeView === 'trends' ? 'active' : ''}`}
          onClick={() => setActiveView('trends')}
        >
          <span className="icon">📈</span>
          Tendances
        </button>
        <button
          className={`nav-btn ${activeView === 'ingredients' ? 'active' : ''}`}
          onClick={() => setActiveView('ingredients')}
        >
          <span className="icon">🥗</span>
          Détails Ingrédients
        </button>
        <button
          className={`nav-btn ${activeView === 'details' ? 'active' : ''}`}
          onClick={() => setActiveView('details')}
        >
          <span className="icon">📋</span>
          Détails Complets
        </button>
      </nav>

      {/* Vue d'ensemble */}
      {activeView === 'overview' && (
        <div className="overview-view">
          {/* Cartes de statistiques principales */}
          <div className="stats-grid">
            <div className="stat-card primary">
              <div className="stat-icon">📊</div>
              <div className="stat-content">
                <h3>Jours analysés</h3>
                <div className="stat-value">{comparisonData?.summary?.totalDays || 0}</div>
                <div className="stat-period">{dateRange.start} → {dateRange.end}</div>
              </div>
            </div>

            <div className="stat-card success">
              <div className="stat-icon">✅</div>
              <div className="stat-content">
                <h3>Taux de Conformité</h3>
                <div className="stat-value">{comparisonData?.summary?.averageComplianceRate ?? '0.00'}%</div>
                <div className="stat-period">Écarts à zéro sur la période</div>
              </div>
            </div>

            <div className="stat-card danger">
              <div className="stat-icon">⚠️</div>
              <div className="stat-content">
                <h3>Pertes total (DT)</h3>
                <div className="stat-value">{formatCurrency(Math.abs(lossesData?.summary?.totalLosses?.value || 0))}</div>
                <div className="stat-subvalue">{formatNumber(Math.abs(lossesData?.summary?.totalLosses?.quantity || 0))} unités • qté × prix moyen d&apos;achat</div>
              </div>
            </div>

            <div className="stat-card warning">
              <div className="stat-icon">📦</div>
              <div className="stat-content">
                <h3>Articles Inventoriés</h3>
                <div className="stat-value">{formatNumber(comparisonData?.summary?.totalItemsInventoried || 0)}</div>
                <div className="stat-subvalue">{comparisonData?.summary?.totalInventories || 0} inventaires</div>
              </div>
            </div>
          </div>

          {/* Graphiques principaux */}
          <div className="charts-row">
            <div className="chart-card">
              <div className="chart-header">
                <h3>Évolution des Écarts Journaliers</h3>
                <div className="chart-legend">
                  <span className="legend-item"><span className="legend-color loss"></span> Pertes</span>
                  <span className="legend-item"><span className="legend-color surplus"></span> Surplus</span>
                  <span className="legend-item"><span className="legend-color total"></span> Total</span>
                </div>
              </div>
              {comparisonData?.dailyAnalysis && (
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={comparisonData.dailyAnalysis}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                    <XAxis
                      dataKey="date"
                      tick={{ fill: '#666' }}
                      axisLine={{ stroke: '#ccc' }}
                    />
                    <YAxis
                      tick={{ fill: '#666' }}
                      axisLine={{ stroke: '#ccc' }}
                    />
                    <Tooltip
                      formatter={(value, name) => [
                        formatNumber(value),
                        name === 'totalDifference' ? 'Écart total' :
                          name === 'positiveDifferences' ? 'Surplus' : 'Pertes'
                      ]}
                      labelFormatter={(label) => `Date: ${label}`}
                      contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                    />
                    <Area
                      type="monotone"
                      dataKey="totalDifference"
                      stroke={CHART_COLORS.primary}
                      fill={CHART_COLORS.primary}
                      fillOpacity={0.2}
                      strokeWidth={2}
                      name="Écart total"
                    />
                    <Area
                      type="monotone"
                      dataKey="positiveDifferences"
                      stroke={CHART_COLORS.success}
                      fill={CHART_COLORS.success}
                      fillOpacity={0.2}
                      strokeWidth={2}
                      name="Surplus"
                    />
                    <Area
                      type="monotone"
                      dataKey="negativeDifferences"
                      stroke={CHART_COLORS.danger}
                      fill={CHART_COLORS.danger}
                      fillOpacity={0.2}
                      strokeWidth={2}
                      name="Pertes"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </div>

            <div className="chart-card">
              <div className="chart-header">
                <h3>Répartition Pertes vs Surplus (Valeur DT)</h3>
              </div>
              {lossesData && (() => {
                const lossVal = Math.abs(lossesData.summary?.totalLosses?.value || 0);
                const surplusVal = lossesData.summary?.totalSurpluses?.value || 0;
                const total = lossVal + surplusVal;
                const pieData = [
                  { name: 'Pertes', value: lossVal, color: CHART_COLORS.danger },
                  { name: 'Surplus', value: surplusVal, color: CHART_COLORS.success }
                ].filter((d) => d.value > 0);
                if (pieData.length === 0) {
                  return (
                    <div className="chart-placeholder">
                      <p>Aucune perte ni surplus sur la période</p>
                    </div>
                  );
                }
                return (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={pieData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name}\n${(percent * 100).toFixed(1)}%`}
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {pieData.map((d, i) => (
                          <Cell key={i} fill={d.color} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value, name) => [formatCurrency(value), name]} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                );
              })()}
            </div>
          </div>

          {/* Top 5 ingrédients avec écarts */}
          <div className="ingredient-summary">
            <div className="section-header">
              <h3>
                <span className="icon">🏆</span>
                Top 5 Ingrédients avec Écarts
              </h3>
              <button
                className="btn-more"
                onClick={() => setActiveView('ingredients')}
              >
                Voir tous les ingrédients →
              </button>
            </div>

            {Object.keys(ingredientDetails).length > 0 ? (
              <div className="ingredient-cards">
                {getTopIngredients()
                  .slice(0, 5)
                  .map((ingredient, index) => (
                    <div
                      key={ingredient.id}
                      className={`ingredient-card ${ingredient.totalDifference < 0 ? 'loss' :
                          ingredient.totalDifference > 0 ? 'surplus' : 'neutral'
                        }`}
                      onClick={() => toggleIngredientExpansion(ingredient.id)}
                    >
                      <div className="ingredient-header">
                        <div className="ingredient-rank">{index + 1}</div>
                        <div className="ingredient-name-container">
                          <h4>{ingredient.name}</h4>
                          <span className="ingredient-unit">{ingredient.unit}</span>
                        </div>
                        <div className={`ingredient-difference ${ingredient.totalDifference < 0 ? 'negative' :
                            ingredient.totalDifference > 0 ? 'positive' : 'neutral'
                          }`}>
                          {ingredient.totalDifference > 0 ? '+' : ''}{formatNumber(ingredient.totalDifference)}
                        </div>
                      </div>

                      {expandedIngredient === ingredient.id && (
                        <div className="ingredient-details">
                          <div className="detail-row">
                            <span className="detail-label">Moyenne journalière:</span>
                            <span className={`detail-value ${ingredient.averageDifference < 0 ? 'negative' :
                                ingredient.averageDifference > 0 ? 'positive' : 'neutral'
                              }`}>
                              {ingredient.averageDifference > 0 ? '+' : ''}{formatNumber(ingredient.averageDifference)}
                            </span>
                          </div>
                          <div className="detail-row">
                            <span className="detail-label">Jours analysés:</span>
                            <span className="detail-value">{ingredient.days.length}</span>
                          </div>
                          <div className="detail-row">
                            <span className="detail-label">Stock moyen:</span>
                            <span className="detail-value">{formatNumber(ingredient.totalCurrent / ingredient.days.length)}</span>
                          </div>
                          {(ingredient.totalLossValue || 0) > 0 && (
                            <div className="detail-row">
                              <span className="detail-label">Valeur perte (DT):</span>
                              <span className="detail-value negative">{formatCurrency(ingredient.totalLossValue)}</span>
                            </div>
                          )}

                          <div className="daily-breakdown">
                            <h5>Détails par jour:</h5>
                            <div className="daily-list">
                              {ingredient.days.map((day, dayIndex) => (
                                <div key={dayIndex} className="daily-item">
                                  <span className="daily-date">{day.date}</span>
                                  <span className="daily-stock">{formatNumber(day.currentStock)} → {formatNumber(day.recordedQuantity)}</span>
                                  <span className={`daily-difference ${day.difference < 0 ? 'negative' :
                                      day.difference > 0 ? 'positive' : 'neutral'
                                    }`}>
                                    {day.difference > 0 ? '+' : ''}{formatNumber(day.difference)}
                                    {day.percentage !== 0 && (
                                      <span className="daily-percentage"> ({day.percentage}%)</span>
                                    )}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))
                }
              </div>
            ) : (
              <div className="no-data">
                <p>Aucune donnée d'ingrédient disponible pour cette période</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Vue par Localisation */}
      {activeView === 'locations' && (
        <div className="locations-view">
          <div className="section-header">
            <h2>
              <span className="icon">📍</span>
              Analyse par Localisation
            </h2>
            <p className="section-subtitle">
              Performance et écarts par site de stockage
            </p>
          </div>

          {/* Statistiques globales des localisations */}
          <div className="locations-stats-grid">
            <div className="stat-card primary">
              <div className="stat-icon">🏢</div>
              <div className="stat-content">
                <h3>Localisations analysées</h3>
                <div className="stat-value">{locations.length}</div>
                <div className="stat-period">Nombre total de sites</div>
              </div>
            </div>

            <div className="stat-card success">
              <div className="stat-icon">📊</div>
              <div className="stat-content">
                <h3>Localisation la plus précise</h3>
                {locations.length > 0 && (() => {
                  const sorted = [...locations].sort((a, b) =>
                    Math.abs(a.averageDifference || 0) - Math.abs(b.averageDifference || 0)
                  );
                  const best = sorted[0];
                  return (
                    <>
                      <div className="stat-value">{best?.name}</div>
                      <div className="stat-subvalue">
                        Écart moyen: {formatNumber(best?.averageDifference ?? 0)}
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>

            <div className="stat-card warning">
              <div className="stat-icon">⚠️</div>
              <div className="stat-content">
                <h3>Localisation avec plus de pertes</h3>
                {locations.length > 0 && (
                  <>
                    <div className="stat-value">
                      {/* Créer une copie avant de trier */}
                      {[...locations]
                        .sort((a, b) => a.totalDifference - b.totalDifference)[0]?.name}
                    </div>
                    <div className="stat-subvalue">
                      Pertes: {formatNumber(
                        Math.abs([...locations]
                          .sort((a, b) => a.totalDifference - b.totalDifference)[0]?.totalDifference || 0)
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Liste des localisations */}
          <div className="locations-list">
            <h3>Détails par Localisation</h3>

            {locations.length > 0 ? (
              <div className="locations-table-container">
                <table className="locations-table">
                  <thead>
                    <tr>
                      <th>Localisation</th>
                      <th>Articles total</th>
                      <th>Jours analysés</th>
                      <th>Différence totale</th>
                      <th>Écart moyen/jour</th>
                      <th>Impact valeur (DT)</th>
                      <th>Performance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {locations.map(location => (
                      <tr key={location.id} onClick={() => setSelectedLocation(location.id)}>
                        <td className="location-name-cell">
                          <div className="location-name">
                            <span className="location-icon">📍</span>
                            {location.name}
                          </div>
                        </td>
                        <td>{location.totalItems}</td>
                        <td>{location.days.length}</td>
                        <td className={location.totalDifference < 0 ? 'negative' : location.totalDifference > 0 ? 'positive' : 'neutral'}>
                          {location.totalDifference > 0 ? '+' : ''}{formatNumber(location.totalDifference)}
                        </td>
                        <td className={location.averageDifference < 0 ? 'negative' : location.averageDifference > 0 ? 'positive' : 'neutral'}>
                          {location.averageDifference > 0 ? '+' : ''}{formatNumber(location.averageDifference)}
                        </td>
                        <td>{formatCurrency(location.totalValueDifference)}</td>
                        <td>
                          <div className="performance-indicator">
                            <div
                              className="performance-bar"
                              style={{
                                width: `${(() => {
                                  const max = Math.max(1, ...locations.map(l => Math.abs(l.totalDifference || 0)));
                                  return Math.min(100, (Math.abs(location.totalDifference || 0) / max) * 100);
                                })()}%`,
                                backgroundColor: location.totalDifference < 0 ? CHART_COLORS.danger : CHART_COLORS.success
                              }}
                            ></div>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="no-data">
                <div className="no-data-icon">📍</div>
                <h3>Aucune donnée de localisation disponible</h3>
                <p>Les données de localisation n'ont pas pu être chargées pour cette période.</p>
              </div>
            )}
          </div>

          {/* Graphique comparatif des localisations */}
          {locations.length > 0 && (
            <div className="locations-chart">
              <h3>Comparatif des Écarts par Localisation</h3>
              <div className="chart-container">
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={locations}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="name"
                      angle={-45}
                      textAnchor="end"
                      height={100}
                      tick={{ fontSize: 12 }}
                    />
                    <YAxis />
                    <Tooltip
                      formatter={(value, name) => [
                        name === 'totalDifference' ? formatNumber(value) : formatCurrency(value),
                        name === 'totalDifference' ? 'Différence totale' : 'Impact valeur'
                      ]}
                    />
                    <Legend />
                    <Bar
                      dataKey="totalDifference"
                      name="Différence totale"
                      fill={CHART_COLORS.primary}
                      radius={[4, 4, 0, 0]}
                    />
                    <Bar
                      dataKey="totalValueDifference"
                      name="Impact valeur (DT)"
                      fill={CHART_COLORS.secondary}
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Vue Analyse des Pertes */}
      {activeView === 'losses' && (
        <div className="losses-view">
          <div className="section-header">
            <h2>
              <span className="icon">📉</span>
              Analyse des Pertes (Valeur en DT)
            </h2>
            <p className="section-subtitle">
              Valeur des pertes = quantité perdue × prix moyen d&apos;achat (Ingredient ou Stock)
            </p>
          </div>

          <div className="losses-stats-grid">
            <div className="stat-card danger">
              <div className="stat-icon">💰</div>
              <div className="stat-content">
                <h3>Somme globale des pertes (DT)</h3>
                <div className="stat-value">
                  {formatCurrency(Math.abs(lossesData?.summary?.totalLosses?.value || 0))}
                </div>
                <div className="stat-subvalue">
                  {formatNumber(Math.abs(lossesData?.summary?.totalLosses?.quantity || 0))} unités
                </div>
              </div>
            </div>
            <div className="stat-card success">
              <div className="stat-icon">📈</div>
              <div className="stat-content">
                <h3>Valeur des surplus (DT)</h3>
                <div className="stat-value">
                  {formatCurrency(lossesData?.summary?.totalSurpluses?.value || 0)}
                </div>
                <div className="stat-subvalue">
                  {formatNumber(lossesData?.summary?.totalSurpluses?.quantity || 0)} unités
                </div>
              </div>
            </div>
          </div>

          <div className="losses-table-section">
            <h3>Valeur de la perte (DT) par ingrédient</h3>
            {lossesData?.summary?.byIngredient?.length > 0 ? (
              <div className="losses-table-container">
                <table className="losses-table">
                  <thead>
                    <tr>
                      <th>Ingrédient</th>
                      <th>Quantité perdue</th>
                      <th>Prix moyen (DT)</th>
                      <th>Valeur perte (DT)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lossesData.summary.byIngredient
                      .filter((ing) => (ing.valueLoss || 0) > 0)
                      .map((ing) => (
                        <tr key={ing.ingredientId || ing.ingredientName}>
                          <td className="ingredient-name-cell">{ing.ingredientName}</td>
                          <td>{formatNumber(ing.totalLoss || 0)} {ing.unit || ''}</td>
                          <td>{formatNumber(ing.valueLoss && ing.totalLoss ? ing.valueLoss / ing.totalLoss : 0)}</td>
                          <td className="value-loss-cell">{formatCurrency(ing.valueLoss || 0)}</td>
                        </tr>
                      ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan="3"><strong>Total pertes (DT)</strong></td>
                      <td className="value-loss-cell">
                        <strong>{formatCurrency(Math.abs(lossesData?.summary?.totalLosses?.value || 0))}</strong>
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            ) : (
              <div className="no-data">
                <div className="no-data-icon">📉</div>
                <h3>Aucune perte avec valeur sur la période</h3>
                <p>Les pertes sont calculées avec &laquo; quantité perdue × prix moyen d&apos;achat &raquo; (Ingredient ou Stock).</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Vue Tendances */}
      {activeView === 'trends' && (
        <div className="trends-view">
          <div className="section-header">
            <h2>
              <span className="icon">📈</span>
              Tendances & Évolution journalière
            </h2>
            <p className="section-subtitle">
              Résumé jour par jour : écarts, conformité et impact valeur (DT)
            </p>
          </div>

          {comparisonData?.dailyAnalysis?.length > 0 ? (
            <div className="trends-table-container">
              <table className="trends-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Inventaires</th>
                    <th>Articles</th>
                    <th>Écart total</th>
                    <th>Nb. pertes</th>
                    <th>Nb. surplus</th>
                    <th>Conformité %</th>
                    <th>Impact valeur (DT)</th>
                  </tr>
                </thead>
                <tbody>
                  {comparisonData.dailyAnalysis.map((day) => {
                    const compliance = day.totalItems > 0
                      ? ((day.zeroDifferences || 0) / day.totalItems * 100).toFixed(1)
                      : '0.0';
                    return (
                      <tr key={day.date}>
                        <td>{day.date}</td>
                        <td>{Array.isArray(day.inventories) ? day.inventories.length : 0}</td>
                        <td>{formatNumber(day.totalItems || 0)}</td>
                        <td className={day.totalDifference < 0 ? 'negative' : day.totalDifference > 0 ? 'positive' : 'neutral'}>
                          {day.totalDifference > 0 ? '+' : ''}{formatNumber(day.totalDifference || 0)}
                        </td>
                        <td className="negative">{formatNumber(day.negativeDifferences || 0)}</td>
                        <td className="positive">{formatNumber(day.positiveDifferences || 0)}</td>
                        <td>{compliance}%</td>
                        <td>{formatCurrency(day.totalValueDifference || 0)}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr>
                    <td><strong>Total / Moyenne</strong></td>
                    <td>{comparisonData.summary?.totalInventories ?? 0}</td>
                    <td>{formatNumber(comparisonData.summary?.totalItemsInventoried || 0)}</td>
                    <td className={(() => {
                      const sum = (comparisonData.dailyAnalysis || []).reduce((s, d) => s + (d.totalDifference || 0), 0);
                      return sum < 0 ? 'negative' : sum > 0 ? 'positive' : 'neutral';
                    })()}>
                      {formatNumber((comparisonData.dailyAnalysis || []).reduce((s, d) => s + (d.totalDifference || 0), 0))}
                    </td>
                    <td colSpan="2">—</td>
                    <td><strong>{comparisonData.summary?.averageComplianceRate ?? '0.00'}%</strong></td>
                    <td><strong>{formatCurrency(comparisonData.summary?.totalValueDifference || 0)}</strong></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          ) : (
            <div className="no-data">
              <div className="no-data-icon">📈</div>
              <h3>Aucune donnée de tendances</h3>
              <p>Choisissez une période avec des inventaires (ex. 7 ou 30 derniers jours) pour afficher l&apos;évolution journalière.</p>
              <button className="btn-primary" onClick={fetchDashboardData}>
                <span className="icon">🔄</span> Actualiser
              </button>
            </div>
          )}
        </div>
      )}

      {/* Vue Détails Complets */}
      {activeView === 'details' && (
        <div className="details-view">
          <div className="section-header">
            <h2>
              <span className="icon">📋</span>
              Détails Complets – Écarts (Pertes & Surplus)
            </h2>
            <p className="section-subtitle">
              Liste détaillée de chaque écart par date, ingrédient, quantité et valeur (DT)
            </p>
          </div>

          {(() => {
            const losses = lossesData?.topLosses || [];
            const surpluses = lossesData?.topSurpluses || [];
            const allRows = [
              ...losses.map((r) => ({ ...r, type: 'Perte', typeClass: 'negative' })),
              ...surpluses.map((r) => ({ ...r, type: 'Surplus', typeClass: 'positive' }))
            ].sort((a, b) => {
              const d = (a.date || '').localeCompare(b.date || '');
              if (d !== 0) return d;
              return (b.valueImpact || 0) - (a.valueImpact || 0);
            });
            return allRows.length > 0 ? (
              <div className="details-table-container">
                <table className="details-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Ingrédient</th>
                      <th>Type</th>
                      <th>Quantité</th>
                      <th>Valeur (DT)</th>
                      <th>Unité</th>
                      <th>Localisation</th>
                      <th>Stock physique</th>
                      <th>Stock enregistré</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allRows.map((row, idx) => (
                      <tr key={`${row.date}-${row.ingredientId || row.ingredientName}-${idx}`}>
                        <td>{row.date}</td>
                        <td className="ingredient-name-cell">{row.ingredientName}</td>
                        <td className={row.typeClass}><strong>{row.type}</strong></td>
                        <td className={row.typeClass}>{row.difference > 0 ? '+' : ''}{formatNumber(row.difference || 0)}</td>
                        <td>{formatCurrency(row.valueImpact || 0)}</td>
                        <td>{row.unit || '—'}</td>
                        <td>{row.location || '—'}</td>
                        <td>{formatNumber(row.currentStock)}</td>
                        <td>{formatNumber(row.recordedQuantity)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan="3"><strong>Total</strong></td>
                      <td>—</td>
                      <td><strong>{formatCurrency(
                        (lossesData?.summary?.totalLosses?.value || 0) + (lossesData?.summary?.totalSurpluses?.value || 0)
                      )}</strong></td>
                      <td colSpan="4"></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            ) : (
              <div className="no-data">
                <div className="no-data-icon">📋</div>
                <h3>Aucun écart détaillé</h3>
                <p>Aucune perte ni surplus enregistré pour la période. Les écarts sont listés ici lorsqu&apos;ils atteignent le seuil minimal.</p>
                <button className="btn-primary" onClick={fetchDashboardData}>
                  <span className="icon">🔄</span> Actualiser
                </button>
              </div>
            );
          })()}
        </div>
      )}

      {/* Vue Détails Ingrédients */}
      {activeView === 'ingredients' && (
        <div className="ingredients-view">
          <div className="section-header">
            <h2>
              <span className="icon">🥗</span>
              Analyse Détaillée par Ingrédient
            </h2>
            <p className="section-subtitle">
              Écarts journaliers et tendances pour chaque ingrédient
            </p>
          </div>

          {/* Filtres pour les ingrédients */}
          <div className="ingredient-filters">
            <div className="filter-group">
              <label>Trier par:</label>
              <select className="filter-select">
                <option value="difference">Plus grand écart</option>
                <option value="name">Nom d'ingrédient</option>
                <option value="consistency">Consistance</option>
              </select>
            </div>

            <div className="filter-group">
              <label>Type d'écart:</label>
              <select className="filter-select">
                <option value="all">Tous</option>
                <option value="loss">Pertes seulement</option>
                <option value="surplus">Surplus seulement</option>
              </select>
            </div>

            <div className="filter-group">
              <label>Rechercher:</label>
              <input
                type="text"
                placeholder="Nom d'ingrédient..."
                className="filter-search"
              />
            </div>
          </div>

          {/* Liste complète des ingrédients */}
          <div className="ingredient-list">
            {Object.keys(ingredientDetails).length > 0 ? (
              <div className="ingredient-grid">
                {getTopIngredients().map((ingredient, index) => (
                  <div
                    key={ingredient.id}
                    className={`ingredient-detail-card ${ingredient.totalDifference < 0 ? 'loss' :
                        ingredient.totalDifference > 0 ? 'surplus' : 'neutral'
                      } ${expandedIngredient === ingredient.id ? 'expanded' : ''}`}
                    onClick={() => toggleIngredientExpansion(ingredient.id)}
                  >
                    <div className="ingredient-main-info">
                      <div className="ingredient-rank-badge">{index + 1}</div>

                      <div className="ingredient-basic-info">
                        <h3 className="ingredient-title">{ingredient.name}</h3>
                        <div className="ingredient-meta">
                          <span className="meta-item">
                            <span className="meta-icon">📦</span>
                            {ingredient.unit}
                          </span>
                          <span className="meta-item">
                            <span className="meta-icon">📅</span>
                            {ingredient.days.length} jours
                          </span>
                          <span className="meta-item">
                            <span className="meta-icon">📊</span>
                            {Math.abs(ingredient.consistency).toFixed(1)}% consistance
                          </span>
                        </div>
                      </div>

                      <div className="ingredient-stats">
                        <div className="stat-box">
                          <div className="stat-label">Différence totale</div>
                          <div className={`stat-value ${ingredient.totalDifference < 0 ? 'negative' :
                              ingredient.totalDifference > 0 ? 'positive' : 'neutral'
                            }`}>
                            {ingredient.totalDifference > 0 ? '+' : ''}{formatNumber(ingredient.totalDifference)}
                          </div>
                        </div>

                        <div className="stat-box">
                          <div className="stat-label">Moyenne/jour</div>
                          <div className={`stat-value ${ingredient.averageDifference < 0 ? 'negative' :
                              ingredient.averageDifference > 0 ? 'positive' : 'neutral'
                            }`}>
                            {ingredient.averageDifference > 0 ? '+' : ''}{formatNumber(ingredient.averageDifference)}
                          </div>
                        </div>

                        <div className="stat-box">
                          <div className="stat-label">Stock moyen</div>
                          <div className="stat-value neutral">
                            {formatNumber(ingredient.totalCurrent / ingredient.days.length)}
                          </div>
                        </div>

                        {(ingredient.totalLossValue || 0) > 0 && (
                          <div className="stat-box">
                            <div className="stat-label">Valeur perte (DT)</div>
                            <div className="stat-value negative">
                              {formatCurrency(ingredient.totalLossValue)}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {expandedIngredient === ingredient.id && (
                      <div className="ingredient-expanded-details">
                        {/* Graphique d'évolution */}
                        <div className="ingredient-chart">
                          <h4>Évolution des écarts</h4>
                          <ResponsiveContainer width="100%" height={200}>
                            <LineChart data={ingredient.days}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                              <XAxis
                                dataKey="date"
                                tick={{ fontSize: 12 }}
                              />
                              <YAxis
                                tickFormatter={(value) => formatNumber(value)}
                              />
                              <Tooltip
                                formatter={(value) => [formatNumber(value), 'Différence']}
                                labelFormatter={(label) => `Date: ${label}`}
                              />
                              <Line
                                type="monotone"
                                dataKey="difference"
                                stroke={ingredient.totalDifference < 0 ? CHART_COLORS.danger : CHART_COLORS.success}
                                strokeWidth={2}
                                dot={{ r: 4 }}
                                activeDot={{ r: 6 }}
                              />
                            </LineChart>
                          </ResponsiveContainer>
                        </div>

                        {/* Tableau détaillé par jour */}
                        <div className="ingredient-daily-table">
                          <h4>Détails journaliers</h4>
                          <table className="daily-details-table">
                            <thead>
                              <tr>
                                <th>Date</th>
                                <th>Stock système</th>
                                <th>Stock physique</th>
                                <th>Différence</th>
                                <th>Pourcentage</th>
                              </tr>
                            </thead>
                            <tbody>
                              {ingredient.days.map((day, dayIndex) => (
                                <tr key={dayIndex}>
                                  <td>{day.date}</td>
                                  <td>{formatNumber(day.currentStock)}</td>
                                  <td>{formatNumber(day.recordedQuantity)}</td>
                                  <td className={day.difference < 0 ? 'negative' : day.difference > 0 ? 'positive' : 'neutral'}>
                                    {day.difference > 0 ? '+' : ''}{formatNumber(day.difference)}
                                  </td>
                                  <td className={day.percentage < 0 ? 'negative' : day.percentage > 0 ? 'positive' : 'neutral'}>
                                    {day.percentage > 0 ? '+' : ''}{day.percentage}%
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                            <tfoot>
                              <tr>
                                <td colSpan="2"><strong>Totaux:</strong></td>
                                <td><strong>{formatNumber(ingredient.totalCurrent)}</strong></td>
                                <td><strong>{formatNumber(ingredient.totalRecorded)}</strong></td>
                                <td className={ingredient.totalDifference < 0 ? 'negative' : ingredient.totalDifference > 0 ? 'positive' : 'neutral'}>
                                  <strong>{ingredient.totalDifference > 0 ? '+' : ''}{formatNumber(ingredient.totalDifference)}</strong>
                                </td>
                                <td></td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>

                        {/* Analyse */}
                        <div className="ingredient-analysis">
                          <h4>Analyse</h4>
                          <div className="analysis-content">
                            {ingredient.totalDifference < 0 ? (
                              <div className="analysis-warning">
                                <span className="analysis-icon">⚠️</span>
                                <div className="analysis-text">
                                  <strong>Attention:</strong> Cet ingrédient présente des pertes cumulées.
                                  {Math.abs(ingredient.totalDifference) > (ingredient.totalCurrent / ingredient.days.length) * 0.1 &&
                                    " Les pertes représentent plus de 10% du stock moyen. Une vérification approfondie est recommandée."
                                  }
                                </div>
                              </div>
                            ) : ingredient.totalDifference > 0 ? (
                              <div className="analysis-success">
                                <span className="analysis-icon">✅</span>
                                <div className="analysis-text">
                                  <strong>Bon point:</strong> Cet ingrédient présente des surplus cumulés.
                                  {ingredient.consistency > 80 &&
                                    " Les écarts sont très cohérents d'un jour à l'autre."
                                  }
                                </div>
                              </div>
                            ) : (
                              <div className="analysis-neutral">
                                <span className="analysis-icon">📊</span>
                                <div className="analysis-text">
                                  <strong>Neutre:</strong> Les écarts sont équilibrés sur la période analysée.
                                </div>
                              </div>
                            )}

                            <div className="analysis-stats">
                              <div className="analysis-stat">
                                <span className="stat-label">Écart maximal:</span>
                                <span className={`stat-value ${Math.max(...ingredient.days.map(d => d.difference)) < 0 ? 'negative' :
                                    Math.max(...ingredient.days.map(d => d.difference)) > 0 ? 'positive' : 'neutral'
                                  }`}>
                                  {formatNumber(Math.max(...ingredient.days.map(d => d.difference)))}
                                </span>
                              </div>
                              <div className="analysis-stat">
                                <span className="stat-label">Écart minimal:</span>
                                <span className={`stat-value ${Math.min(...ingredient.days.map(d => d.difference)) < 0 ? 'negative' :
                                    Math.min(...ingredient.days.map(d => d.difference)) > 0 ? 'positive' : 'neutral'
                                  }`}>
                                  {formatNumber(Math.min(...ingredient.days.map(d => d.difference)))}
                                </span>
                              </div>
                              <div className="analysis-stat">
                                <span className="stat-label">Variation:</span>
                                <span className="stat-value neutral">
                                  {formatNumber(Math.max(...ingredient.days.map(d => d.difference)) - Math.min(...ingredient.days.map(d => d.difference)))}
                                </span>
                              </div>
                              {(ingredient.totalLossValue || 0) > 0 && (
                                <>
                                  <div className="analysis-stat">
                                    <span className="stat-label">Valeur perte (DT):</span>
                                    <span className="stat-value negative">{formatCurrency(ingredient.totalLossValue)}</span>
                                  </div>
                                  {(ingredient.averagePrice || 0) > 0 && (
                                    <div className="analysis-stat">
                                      <span className="stat-label">Prix moyen (DT):</span>
                                      <span className="stat-value neutral">{formatNumber(ingredient.averagePrice)}</span>
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="no-data">
                <div className="no-data-icon">🥗</div>
                <h3>Aucune donnée d'ingrédient disponible</h3>
                <p>Les données d'ingrédients n'ont pas pu être chargées pour cette période.</p>
                <button className="btn-primary" onClick={fetchDashboardData}>
                  <span className="icon">🔄</span>
                  Réessayer le chargement
                </button>
              </div>
            )}
          </div>

          {/* Résumé statistique */}
          {Object.keys(ingredientDetails).length > 0 && (
            <div className="ingredient-summary-stats">
              <h3>Résumé Statistique des Ingrédients</h3>
              <div className="summary-grid">
                <div className="summary-stat">
                  <div className="summary-label">Nombre total d'ingrédients</div>
                  <div className="summary-value">{Object.keys(ingredientDetails).length}</div>
                </div>
                <div className="summary-stat">
                  <div className="summary-label">Ingrédients avec pertes</div>
                  <div className="summary-value negative">
                    {Object.values(ingredientDetails).filter(i => i.totalDifference < 0).length}
                  </div>
                </div>
                <div className="summary-stat">
                  <div className="summary-label">Ingrédients avec surplus</div>
                  <div className="summary-value positive">
                    {Object.values(ingredientDetails).filter(i => i.totalDifference > 0).length}
                  </div>
                </div>
                <div className="summary-stat">
                  <div className="summary-label">Ingrédients conformes</div>
                  <div className="summary-value neutral">
                    {Object.values(ingredientDetails).filter(i => i.totalDifference === 0).length}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Footer informatif */}
      <footer className="dashboard-footer">
        <div className="footer-content">
          <p>
            <strong>Dashboard d'Inventaire</strong> •
            Montants en Dinars Tunisiennes (DT) •
            Dernière mise à jour: {new Date().toLocaleString('fr-FR')} •
            Données couvrant {dateRange.start} → {dateRange.end}
          </p>
          <p className="footer-note">
            ℹ️ Cliquez sur un ingrédient pour voir ses détails jour par jour.
            Les valeurs positives indiquent des surplus, les valeurs négatives indiquent des pertes.
          </p>
        </div>
      </footer>
      </>
      )}
    </div>
  );
};

export default StockDashboard;